import { useEffect, useMemo, useState } from "react";
import Table from "../../../Components/Table/Table";
import TPSLModal from "./TPSLModal";
import { toJalaliDateTime } from "../../../helper/toJalaliDateTime";
import { useNavigate, useParams } from "react-router-dom";
import SVG from "../../../Components/SVG";
import Spinner from "../../../Components/Loader/Spinner";
import {
  findSymbolData,
  formatTradeSymbol,
  formatTrimmedNumber,
  inferSymbolPricePrecision,
  normalizeSymbolCode,
  resolveSideMarketPrice,
  resolveTradeEntryPrice,
} from "../tradeUtils";
import { number } from "motion";
import { API_BASE_URL } from "../../../config/api";

export default function OrderList({
  accountData,
  marketData,
  symbol,
  className = "",
}) {
  const navigate = useNavigate();
  const { accountId: accountCode } = useParams();
  const [activeTab, setActiveTab] = useState(0);
  const [onlyThisSymbol, setOnlyThisSymbol] = useState(false);
  const [isTPSLModalOpen, setIsTPSLModalOpen] = useState(false);
  const [tp, setTp] = useState(null);
  const [sl, setSl] = useState(null);
  const [ord, setOrd] = useState(null);
  const [tpSlType, setTpSlType] = useState(null);
  const [tpSlMaxDecimals, setTpSlMaxDecimals] = useState(4);
  const [tpSlEstimateSource, setTpSlEstimateSource] = useState(null);
  const [closedTrades, setClosedTrades] = useState([]);
  const [isTradeHistoryLoading, setIsTradeHistoryLoading] = useState(false);
  const [cancelingOrderId, setCancelingOrderId] = useState(null);
  const [closingPositionId, setClosingPositionId] = useState(null);

  const formatSymbol = (value) => formatTradeSymbol(value);
  const formatPrice = (value, fallback = "-") =>
    formatTrimmedNumber(value, {
      useGrouping: true,
      fallback,
      maxFractionDigits: 18,
    });
  const toNullableNumber = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };
  const formatOptionalPrice = (value, fallback = "-") => {
    const parsed = toNullableNumber(value);
    return parsed === null ? fallback : formatPrice(parsed, fallback);
  };
  const formatOrderType = (value) => {
    if (!value) return "";
    return value.replaceAll("_", "").toUpperCase();
  };
  const formatPnl = (value) => {
    const pnl = Number(value);
    if (!Number.isFinite(pnl)) return "-";
    return pnl >= 0
      ? `$${Number(pnl.toFixed(2)).toLocaleString()}`
      : `-$${Number(Math.abs(pnl).toFixed(2)).toLocaleString()}`;
  };

  const openPositions = Array.isArray(accountData?.positions?.open)
    ? accountData.positions.open
    : [];
  const openOrders = Array.isArray(accountData?.orders?.open)
    ? accountData.orders.open
    : [];
  const normalizedSymbol = normalizeSymbolCode(symbol);

  const filteredOpenPositions = useMemo(
    () =>
      onlyThisSymbol
        ? openPositions.filter(
            (item) => normalizeSymbolCode(item?.symbol) === normalizedSymbol,
          )
        : openPositions,
    [normalizedSymbol, onlyThisSymbol, openPositions],
  );

  const filteredOpenOrders = useMemo(
    () =>
      onlyThisSymbol
        ? openOrders.filter(
            (item) => normalizeSymbolCode(item?.symbol) === normalizedSymbol,
          )
        : openOrders,
    [normalizedSymbol, onlyThisSymbol, openOrders],
  );

  const filteredClosedTrades = useMemo(
    () =>
      onlyThisSymbol
        ? closedTrades.filter(
            (item) => normalizeSymbolCode(item?.symbol) === normalizedSymbol,
          )
        : closedTrades,
    [closedTrades, normalizedSymbol, onlyThisSymbol],
  );

  const structuralSignature = useMemo(() => {
    const orderIds = openOrders.map((item) => item?.id).join("|");
    const positionIds = openPositions.map((item) => item?.id).join("|");
    return `${orderIds}::${positionIds}`;
  }, [openOrders, openPositions]);
  const tpSlEstimateContext = useMemo(() => {
    if (!tpSlEstimateSource) return null;

    const symbolData = findSymbolData(
      marketData?.symbols,
      tpSlEstimateSource.rawSymbol,
    );
    const currentMarketPrice = resolveSideMarketPrice(
      symbolData,
      tpSlEstimateSource.rawSide,
    );

    if (tpSlEstimateSource.type === "position") {
      return {
        side: tpSlEstimateSource.rawSide,
        marginUsdt: tpSlEstimateSource.marginUsdt,
        leverage: accountData?.account?.leverage ?? 0,
        entryPrice: tpSlEstimateSource.entryPriceValue,
        marketPrice: currentMarketPrice,
      };
    }

    return {
      side: tpSlEstimateSource.rawSide,
      marginUsdt: tpSlEstimateSource.marginUsdt,
      leverage: accountData?.account?.leverage ?? 0,
      entryPrice: resolveTradeEntryPrice({
        orderType: tpSlEstimateSource.rawOrderType,
        marketPrice: currentMarketPrice,
        limitPrice: tpSlEstimateSource.limitPriceValue,
        stopPrice: tpSlEstimateSource.stopPriceValue,
      }),
      marketPrice: currentMarketPrice,
    };
  }, [accountData, marketData, tpSlEstimateSource]);

  useEffect(() => {
    setClosedTrades([]);
  }, [accountCode]);

  useEffect(() => {
    if (activeTab !== 3 || !accountCode) return;

    let isCancelled = false;

    const fetchTradeHistory = async () => {
      setIsTradeHistoryLoading(true);

      try {
        let response = await fetch(
          `${API_BASE_URL}trading/accounts/${accountCode}/trade-history`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("access")}`,
            },
          },
        );

        if (response.status === 401) {
          const refreshResponse = await fetch(
            `${API_BASE_URL}auth/trader/jwt/refresh/`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                refresh: localStorage.getItem("refresh"),
              }),
            },
          );

          if (!refreshResponse.ok) {
            localStorage.removeItem("access");
            localStorage.removeItem("authCredentials");
            localStorage.removeItem("refresh");
            localStorage.removeItem("role");
            localStorage.removeItem("email");
            navigate("/signin");
            return;
          }

          const refreshData = await refreshResponse.json();
          localStorage.setItem("access", refreshData.access);

          response = await fetch(
            `${API_BASE_URL}trading/accounts/${accountCode}/trade-history`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("access")}`,
              },
            },
          );
        }

        if (!response.ok) {
          throw new Error(`https error! Status: ${response.status}`);
        }

        const data = await response.json();
        if (isCancelled) return;
        setClosedTrades(Array.isArray(data?.trades) ? data.trades : []);
      } catch (error) {
        if (!isCancelled) {
          console.error("Error fetching trade history:", error);
          setClosedTrades([]);
        }
      } finally {
        if (!isCancelled) {
          setIsTradeHistoryLoading(false);
        }
      }
    };

    fetchTradeHistory();

    return () => {
      isCancelled = true;
    };
  }, [accountCode, activeTab, navigate, structuralSignature]);

  const openTPSLModal = (row, type) => {
    const symbolData = findSymbolData(marketData?.symbols, row.rawSymbol);

    setTpSlType(type);
    setOrd(row.id);
    setTp(row.tp);
    setSl(row.sl);
    setTpSlMaxDecimals(inferSymbolPricePrecision(symbolData, 4));
    setTpSlEstimateSource({ ...row, type });
    setIsTPSLModalOpen(true);
  };

  const handleCancel = async (posId) => {
    if (cancelingOrderId) return;

    setCancelingOrderId(String(posId));
    try {
      let response = await fetch(
        `${API_BASE_URL}trading/cancel-order`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access")}`,
          },
          body: JSON.stringify({
            account_id: accountCode,
            order_id: posId,
          }),
        },
      );

      if (response.status === 401) {
        const refreshResponse = await fetch(
          `${API_BASE_URL}auth/trader/jwt/refresh/`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              refresh: localStorage.getItem("refresh"),
            }),
          },
        );

        if (!refreshResponse.ok) {
          localStorage.removeItem("access");
          localStorage.removeItem("authCredentials");
          localStorage.removeItem("refresh");
          localStorage.removeItem("role");
          localStorage.removeItem("email");
          navigate("/signin");
          return;
        }

        const refreshData = await refreshResponse.json();
        localStorage.setItem("access", refreshData.access);

        response = await fetch(
          `${API_BASE_URL}trading/cancel-order`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("access")}`,
            },
            body: JSON.stringify({
              account_id: accountCode,
              order_id: posId,
            }),
          },
        );
      }

      if (!response.ok) {
        throw new Error(`https error! Status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Account Data:", data);
    } catch (error) {
      console.error("Error fetching account data:", error);
    } finally {
      setCancelingOrderId(null);
    }
  };

  const handleClose = async (posId) => {
    if (closingPositionId) return;

    setClosingPositionId(String(posId));
    try {
      let response = await fetch(
        `${API_BASE_URL}trading/close-position`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access")}`,
          },
          body: JSON.stringify({
            account_id: accountCode,
            position_id: posId,
          }),
        },
      );

      if (response.status === 401) {
        const refreshResponse = await fetch(
          `${API_BASE_URL}auth/trader/jwt/refresh/`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              refresh: localStorage.getItem("refresh"),
            }),
          },
        );

        if (!refreshResponse.ok) {
          localStorage.removeItem("access");
          localStorage.removeItem("authCredentials");
          localStorage.removeItem("refresh");
          localStorage.removeItem("role");
          localStorage.removeItem("email");
          navigate("/signin");
          return;
        }

        const refreshData = await refreshResponse.json();
        localStorage.setItem("access", refreshData.access);

        response = await fetch(
          `${API_BASE_URL}trading/close-position`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("access")}`,
            },
            body: JSON.stringify({
              account_id: accountCode,
              position_id: posId,
            }),
          },
        );
      }

      if (!response.ok) {
        throw new Error(`https error! Status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Account Data:", data);
    } catch (error) {
      console.error("Error fetching account data:", error);
    } finally {
      setClosingPositionId(null);
    }
  };

  const positionRows = filteredOpenPositions.map((pos) => ({
    id: pos.id,
    rawSymbol: pos.symbol,
    rawSide: pos.side,
    tp: toNullableNumber(pos.tp),
    sl: toNullableNumber(pos.sl),
    marginUsdt: toNullableNumber(pos.margin_usdt),
    entryPriceValue: toNullableNumber(pos.entry_price),
    date: toJalaliDateTime(pos.opened_at),
    symbol: formatSymbol(pos.symbol),
    type: formatOrderType(pos.order_type),
    side: pos.side.toUpperCase(),
    entryPrice: `$${formatPrice(pos.entry_price)}`,
    totalCost: `$${Number(pos.margin_usdt).toLocaleString()}`,
    volume: Number(pos.qty).toFixed(5),
    takeProfit: formatOptionalPrice(pos.tp),
    stopLoss: formatOptionalPrice(pos.sl),
    pnl: formatPnl(pos.unrealized_pnl),
    numberPnl: Number(pos.unrealized_pnl),
    operation: "true",
  }));

  const openOrderRows = filteredOpenOrders.map((ord) => ({
    id: `${ord.id}`,
    rawSymbol: ord.symbol,
    rawSide: ord.side,
    rawOrderType: ord.type ?? ord.order_type,
    tp: toNullableNumber(ord.tp),
    sl: toNullableNumber(ord.sl),
    marginUsdt: toNullableNumber(ord.margin_usdt),
    stopPriceValue: toNullableNumber(ord.stop_price),
    limitPriceValue: toNullableNumber(ord.limit_price),
    date: toJalaliDateTime(ord.created_at),
    symbol: formatSymbol(ord.symbol),
    type: formatOrderType(ord.type),
    side: ord.side.toUpperCase(),
    stopPrice: ord.stop_price ? formatPrice(ord.stop_price) : "-",
    orderPrice: ord.limit_price ? formatPrice(ord.limit_price) : "-",
    totalCost: `$${Number(ord.margin_usdt).toLocaleString()}`,
    takeProfit: formatOptionalPrice(ord.tp),
    stopLoss: formatOptionalPrice(ord.sl),
    operation: "true",
  }));

  const closedTradeRows = filteredClosedTrades.map((trade) => ({
    openDate: toJalaliDateTime(trade.opened_at),
    closeDate: toJalaliDateTime(trade.closed_at),
    symbol: formatSymbol(trade.symbol),
    type: formatOrderType(trade.order_type),
    side: trade.side,
    entryPrice: formatPrice(trade.entry_price),
    exitPrice: formatPrice(trade.exit_price),
    totalCost: `$${Number(trade.margin_usdt).toLocaleString()}`,
    volume: Number(trade.volume).toFixed(5),
    pnl: formatPnl(trade.profit_loss),
    numberPnl: Number(trade.profit_loss),
  }));

  return (
    <div
      className={`w-full rounded-2xl py-2.5 px-5 flex flex-col bg-zi-bg2-light dark:bg-zi-bg2-dark divide-y divide-zi-border-dark/50 dark:divide-zi-border-light/50 ${className}`}
    >
      <div className="w-full flex items-center justify-between gap-14 min-w-0 overflow-x-auto text-zi-text-light dark:text-zi-text-dark">
        <div className="flex items-center gap-7 text-sm">
          <button
            type="button"
            onClick={() => setActiveTab(0)}
            className={`pb-2.5 border-b-2 text-nowrap ${
              activeTab === 0
                ? "text-zi-text-light dark:text-zi-text-dark border-zi-border-dark dark:border-zi-border-light"
                : "text-zi-text-light/50 dark:text-zi-text-dark/50 hocus:text-zi-text-light hocus:dark:text-zi-text-dark border-transparent"
            }`}
          >
            پوزیشن‌ها ({filteredOpenPositions.length})
          </button>
          <button
            type="button"
            onClick={() => setActiveTab(1)}
            className={`pb-2.5 border-b-2 text-nowrap ${
              activeTab === 1
                ? "text-zi-text-light dark:text-zi-text-dark border-zi-border-dark dark:border-zi-border-light "
                : "text-zi-text-light/50 dark:text-zi-text-dark/50 hocus:text-zi-text-light hocus:dark:text-zi-text-dark border-transparent"
            }`}
          >
            ‌سفارشات باز ({filteredOpenOrders.length})
          </button>
          <button
            type="button"
            onClick={() => setActiveTab(3)}
            className={`pb-2.5 border-b-2 text-nowrap ${
              activeTab === 3
                ? "text-zi-text-light dark:text-zi-text-dark border-zi-border-dark dark:border-zi-border-light "
                : "text-zi-text-light/50 dark:text-zi-text-dark/50 hocus:text-zi-text-light hocus:dark:text-zi-text-dark border-transparent"
            }`}
          >
            تاریخچه معاملات
          </button>
        </div>
        <button
          type="button"
          className="flex text-nowrap items-center gap-2 text-xs pb-2.5"
          onClick={() => setOnlyThisSymbol((prev) => !prev)}
        >
          <span className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-[4px] border border-zi-border-dark dark:border-zi-border-light text-[10px]">
            {onlyThisSymbol ? "✓" : ""}
          </span>
          <span>فقط نمایش این نماد</span>
        </button>
      </div>
      {activeTab === 0 && (
        <div className="pt-2.5">
          <Table
            isEnglish={[7]}
            isLTR={true}
            haveScroll={true}
            maxHeight="260px"
            columns={[
              { key: "date", label: "تاریخ" },
              { key: "symbol", label: "نماد" },
              { key: "type", label: "نوع" },
              { key: "side", label: "ساید" },
              { key: "entryPrice", label: "قیمت ورود" },
              { key: "totalCost", label: "مقدار" },
              { key: "volume", label: "حجم" },
              { key: "takeProfit", label: "حد سود" },
              { key: "stopLoss", label: "حد ضرر" },
              {
                key: "pnl",
                label: "میزان سود",
                render: (value, row) => {
                  if (row.numberPnl && row.numberPnl > 0) {
                    return (
                      <div className="py-1 px-2 rounded-md bg-zi-green-dark/10 ring-1 ring-inset ring-zi-green-dark text-zi-green-dark">
                        <p style={{ direction: "ltr" }}>{value}</p>
                      </div>
                    );
                  } else if (row.numberPnl && row.numberPnl < 0) {
                    return (
                      <div className="py-1 px-2 rounded-md bg-zi-red-dark/10 ring-1 ring-inset ring-zi-red-dark text-zi-red-dark">
                        <p style={{ direction: "ltr" }}>{value}</p>
                      </div>
                    );
                  } else {
                    return (
                      <div className="py-1 px-2 rounded-md">
                        <p style={{ direction: "ltr" }}>{value}</p>
                      </div>
                    );
                  }
                },
              },
              {
                key: "operation",
                label: "عملیات",
                render: (value, row) =>
                  value && (
                    <div className="justify-center items-center flex gap-5 [direction:ltr]">
                      <button
                        onClick={() => handleClose(row.id)}
                        disabled={closingPositionId === String(row.id)}
                        className={`min-w-8 inline-flex justify-center disabled:cursor-wait ${
                          closingPositionId === String(row.id)
                            ? ""
                            : "underline underline-offset-2 text-zi-red-dark hocus:underline-offset-8"
                        }`}
                      >
                        {closingPositionId === String(row.id) ? (
                          <Spinner size="20" color="#D71F1F" stroke="3" />
                        ) : (
                          "بستن"
                        )}
                      </button>
                      <button
                        className="underline underline-offset-2 hocus:underline-offset-8"
                        onClick={() => openTPSLModal(row, "position")}
                      >
                        ویرایش
                      </button>
                    </div>
                  ),
              },
            ]}
            data={positionRows}
          />
          {filteredOpenPositions.length === 0 && (
            <div className="w-full flex justify-center items-center flex-col gap-5 pt-5">
              <p className="text-zi-text-light dark:text-zi-text-dark">
                موردی برای نمایش وجود ندارد
              </p>
              <SVG name="noData" classname="w-24 fill-cyan-500" />
            </div>
          )}
        </div>
      )}
      {activeTab === 1 && (
        <div className="pt-2.5">
          <Table
            isLTR={true}
            haveScroll={true}
            maxHeight="260px"
            columns={[
              { key: "date", label: "تاریخ" },
              { key: "symbol", label: "نماد" },
              { key: "type", label: "نوع" },
              { key: "side", label: "ساید" },
              { key: "stopPrice", label: "قیمت توقف" },
              { key: "orderPrice", label: "قیمت سفارش" },
              { key: "totalCost", label: "مقدار" },
              { key: "takeProfit", label: "حد سود" },
              { key: "stopLoss", label: "حد ضرر" },
              {
                key: "operation",
                label: "عملیات",
                render: (value, row) =>
                  value && (
                    <div className="justify-center items-center flex gap-5 [direction:ltr]">
                      <button
                        onClick={() => handleCancel(row.id)}
                        disabled={cancelingOrderId === String(row.id)}
                        className={`min-w-8 inline-flex justify-center disabled:cursor-wait ${
                          cancelingOrderId === String(row.id)
                            ? ""
                            : "underline underline-offset-2 text-zi-red-dark hocus:underline-offset-8"
                        }`}
                      >
                        {cancelingOrderId === String(row.id) ? (
                          <Spinner size="20" color="#D71F1F" stroke="3" />
                        ) : (
                          "لغو"
                        )}
                      </button>
                      <button
                        className="underline underline-offset-2 hocus:underline-offset-8"
                        onClick={() => openTPSLModal(row, "order")}
                      >
                        ویرایش
                      </button>
                    </div>
                  ),
              },
            ]}
            data={openOrderRows}
          />
          {filteredOpenOrders.length === 0 && (
            <div className="w-full flex justify-center items-center flex-col gap-5 pt-5">
              <p className="text-zi-text-light dark:text-zi-text-dark">
                موردی برای نمایش وجود ندارد
              </p>
              <SVG name="noData" classname="w-24 fill-cyan-500" />
            </div>
          )}
        </div>
      )}
      {activeTab === 3 && (
        <div className="pt-2.5">
          <Table
            isEnglish={[8]}
            isLTR={true}
            haveScroll={true}
            maxHeight="260px"
            columns={[
              { key: "openDate", label: "تاریخ ورود" },
              { key: "closeDate", label: "تاریخ خروج" },
              { key: "symbol", label: "نماد" },
              { key: "type", label: "نوع" },
              { key: "side", label: "ساید" },
              { key: "entryPrice", label: "قیمت ورود" },
              { key: "exitPrice", label: "قیمت خروج" },
              { key: "totalCost", label: "مقدار" },
              { key: "volume", label: "حجم" },
              {
                key: "pnl",
                label: "میزان سود",
                render: (value, row) => {
                  if (row.numberPnl && row.numberPnl > 0) {
                    return (
                      <div className="py-1 px-2 rounded-md bg-zi-green-dark/10 ring-1 ring-inset ring-zi-green-dark text-zi-green-dark">
                        <p style={{ direction: "ltr" }}>{value}</p>
                      </div>
                    );
                  } else if (row.numberPnl && row.numberPnl < 0) {
                    return (
                      <div className="py-1 px-2 rounded-md bg-zi-red-dark/10 ring-1 ring-inset ring-zi-red-dark text-zi-red-dark">
                        <p style={{ direction: "ltr" }}>{value}</p>
                      </div>
                    );
                  } else {
                    return (
                      <div className="py-1 px-2 rounded-md">
                        <p style={{ direction: "ltr" }}>{value}</p>
                      </div>
                    );
                  }
                },
              },
            ]}
            data={closedTradeRows}
          />
          {isTradeHistoryLoading && (
            <div className="w-full flex justify-center items-center pt-5">
              <p className="text-zi-text-light dark:text-zi-text-dark">
                در حال بارگذاری
              </p>
            </div>
          )}
          {!isTradeHistoryLoading && filteredClosedTrades.length === 0 && (
            <div className="w-full flex justify-center items-center flex-col gap-5 pt-5">
              <p className="text-zi-text-light dark:text-zi-text-dark">
                موردی برای نمایش وجود ندارد
              </p>
              <SVG name="noData" classname="w-24 fill-cyan-500" />
            </div>
          )}
        </div>
      )}
      {isTPSLModalOpen && (
        <div
          className="w-full fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={() => {
            setIsTPSLModalOpen(false);
            setTpSlEstimateSource(null);
          }}
        >
          <div
            className="w-11/12 max-w-sm flex justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <TPSLModal
              tp={tp}
              sl={sl}
              ord={ord}
              tpSlType={tpSlType}
              estimateContext={tpSlEstimateContext}
              maxPriceDecimals={tpSlMaxDecimals}
              onClose={() => {
                setIsTPSLModalOpen(false);
                setTpSlEstimateSource(null);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
